import { useState, useMemo, useEffect, useRef } from "react";
import reunificacionConHipotecaIcon from "../../assets/calculator/tab-icons/reunificacion-con-hipoteca.png";
import reunificacionSinHipotecaIcon from "../../assets/calculator/tab-icons/reunificacion-sin-hipoteca.png";
import reformaPrestamosIcon from "../../assets/calculator/tab-icons/reforma-prestamos.png";

type LoanType = "hipotecaria" | "sinHipoteca" | "reforma";

interface LoanConfig {
  tae: number;
  minAmount: number;
  maxAmount: number;
  stepAmount: number;
  minMonths: number;
  maxMonths: number;
  timeUnit: "meses" | "años";
  comisionApertura: number;
}

const loanConfigs: Record<LoanType, LoanConfig> = {
  hipotecaria: {
    tae: 3.5,
    minAmount: 80000,
    maxAmount: 600000,
    stepAmount: 5000,
    minMonths: 60,   // 5 años
    maxMonths: 360,   // 30 años
    timeUnit: "años",
    comisionApertura: 1.5,
  },
  sinHipoteca: {
    tae: 11.5,
    minAmount: 4000,
    maxAmount: 90000,
    stepAmount: 500,
    minMonths: 60,
    maxMonths: 120,
    timeUnit: "meses",
    comisionApertura: 2.3,
  },
  reforma: {
    tae: 7.5,
    minAmount: 4000,
    maxAmount: 60000,
    stepAmount: 500,
    minMonths: 60,
    maxMonths: 120,
    timeUnit: "meses",
    comisionApertura: 2.0,
  },
};

const tabs: { id: LoanType; line1: string; line2: string; iconSrc: string }[] = [
  {
    id: "hipotecaria",
    line1: "Reunificación",
    line2: "Con Hipoteca",
    iconSrc: reunificacionConHipotecaIcon,
  },
  {
    id: "sinHipoteca",
    line1: "Reunificación",
    line2: "Sin Hipoteca",
    iconSrc: reunificacionSinHipotecaIcon,
  },
  {
    id: "reforma",
    line1: "Reforma y",
    line2: "Préstamos",
    iconSrc: reformaPrestamosIcon,
  },
];

const formatNumber = (n: number) =>
  n.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 });


// Approximate TIN from TAE (monthly compounding)
const taeToTin = (tae: number) => {
  const r = Math.pow(1 + tae / 100, 1 / 12) - 1;
  return r * 12 * 100;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const parseSpanishNumber = (str: string) => {
  const cleaned = str.replace(/[^0-9]/g, "");
  return cleaned === "" ? NaN : parseInt(cleaned, 10);
};

const LoanCalculator = () => {
  const [activeTab, setActiveTab] = useState<LoanType>("sinHipoteca");
  const config = loanConfigs[activeTab];

  const [amount, setAmount] = useState(config.minAmount);
  const [months, setMonths] = useState(config.maxMonths);

  const [amountInput, setAmountInput] = useState(formatNumber(amount));
  const [monthsInput, setMonthsInput] = useState(
    config.timeUnit === "años" ? String(Math.floor(config.maxMonths / 12)) : String(config.maxMonths)
  );

  const amountInputRef = useRef<HTMLInputElement>(null);
  const monthsInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const c = loanConfigs[activeTab];
    const newAmount = Math.round((c.minAmount + c.maxAmount) / 2 / c.stepAmount) * c.stepAmount;
    const newMonths = c.maxMonths;
    setAmount(newAmount);
    setMonths(newMonths);
    setAmountInput(formatNumber(newAmount));
    setMonthsInput(c.timeUnit === "años" ? String(Math.floor(newMonths / 12)) : String(newMonths));
  }, [activeTab]);

  useEffect(() => {
    if (document.activeElement !== amountInputRef.current) {
      setAmountInput(formatNumber(amount));
    }
  }, [amount]);

  useEffect(() => {
    if (document.activeElement !== monthsInputRef.current) {
      setMonthsInput(
        config.timeUnit === "años" ? String(Math.floor(months / 12)) : String(months)
      );
    }
  }, [months, config.timeUnit]);

  const commitAmount = (raw: string) => {
    const parsed = parseSpanishNumber(raw);
    if (!isNaN(parsed)) {
      const snapped = Math.round(parsed / config.stepAmount) * config.stepAmount;
      const clamped = clamp(snapped, config.minAmount, config.maxAmount);
      setAmount(clamped);
      setAmountInput(formatNumber(clamped));
    } else {
      setAmountInput(formatNumber(amount));
    }
  };

  const commitMonths = (raw: string) => {
    const parsed = parseInt(raw.replace(/[^0-9]/g, ""), 10);
    if (!isNaN(parsed)) {
      const inMonths = config.timeUnit === "años" ? parsed * 12 : parsed;
      const step = config.timeUnit === "años" ? 12 : 1;
      const snapped = Math.round(inMonths / step) * step;
      const clamped = clamp(snapped, config.minMonths, config.maxMonths);
      setMonths(clamped);
      setMonthsInput(
        config.timeUnit === "años" ? String(Math.floor(clamped / 12)) : String(clamped)
      );
    } else {
      setMonthsInput(
        config.timeUnit === "años" ? String(Math.floor(months / 12)) : String(months)
      );
    }
  };

  const TIN = parseFloat(taeToTin(config.tae).toFixed(2));
  const TAE = config.tae;
  const comisionApertura = config.comisionApertura;

  const results = useMemo(() => {
    const monthlyRate = TIN / 100 / 12;
    const n = months;
    const monthlyPayment =
      (amount * monthlyRate * Math.pow(1 + monthlyRate, n)) /
      (Math.pow(1 + monthlyRate, n) - 1);
    const totalRepay = monthlyPayment * n + amount * (comisionApertura / 100);
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    const euros = Math.floor(monthlyPayment);
    const cents = Math.round((monthlyPayment - euros) * 100);

    return { euros, cents, monthlyPayment, totalRepay, years, remainingMonths };
  }, [amount, months, TIN, comisionApertura]);

  const amountProgress = ((amount - config.minAmount) / (config.maxAmount - config.minAmount)) * 100;
  const monthsProgress = ((months - config.minMonths) / (config.maxMonths - config.minMonths)) * 100;

  const timeDisplay = config.timeUnit === "años"
    ? `${results.years} años${results.remainingMonths > 0 ? ` y ${results.remainingMonths} meses` : ""}`
    : `${months} meses`;

  const timeStep = config.timeUnit === "años" ? 12 : 1;

  // "Solicita" now opens the simplified form, carrying over the chosen amount
  // and a matching loan reason.
  const motivoByTab: Record<LoanType, string> = {
    hipotecaria: "Reunificación",
    sinHipoteca: "Reunificación",
    reforma: "Reforma",
  };
  const solicitaUrl = `https://formulariosimple-easydebt.web.app/?importe=${amount}&motivo=${encodeURIComponent(motivoByTab[activeTab])}`;

  return (
    <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Tabs */}
      <div className="grid grid-cols-3 gap-1.5 sm:gap-4 mb-8 sm:mb-12">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex flex-col items-center justify-center gap-2 px-2 py-3 sm:px-4 sm:py-4 rounded-xl border-2 transition-all text-[11px] sm:text-sm font-medium ${
              activeTab === tab.id
                ? "border-primary bg-card shadow-md"
                : "border-border bg-card hover:border-muted-foreground/30"
            }`}
          >
            <span className="flex h-10 items-center justify-center sm:h-12">
              <img
                src={tab.iconSrc}
                alt=""
                aria-hidden="true"
                className={`h-full w-auto max-w-[88px] object-contain transition-transform duration-200 sm:max-w-[104px] ${
                  activeTab === tab.id ? "scale-100" : "scale-[0.96] opacity-90"
                }`}
              />
            </span>
            <span className="text-foreground text-center leading-tight">
              {tab.line1}
              <br />
              {tab.line2}
            </span>
          </button>
        ))}
      </div>

      {/* Main content - responsive grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
        {/* Left: Sliders */}
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-foreground mb-6 sm:mb-8">
            ¿Qué cantidad necesitas y qué plazo se ajusta a tus necesidades?
          </h2>

          {/* Amount slider */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-2">
              <div className="flex-1">
                <input
                  type="range"
                  min={config.minAmount}
                  max={config.maxAmount}
                  step={config.stepAmount}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  style={{ "--slider-progress": `${amountProgress}%` } as React.CSSProperties}
                  className="w-full"
                />
              </div>
              <div className="border border-border rounded-lg w-[130px] shrink-0 text-center bg-card flex items-center">
                <input
                  ref={amountInputRef}
                  type="text"
                  inputMode="numeric"
                  value={amountInput}
                  onChange={(e) => setAmountInput(e.target.value)}
                  onBlur={(e) => commitAmount(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { commitAmount((e.target as HTMLInputElement).value); (e.target as HTMLInputElement).blur(); } }}
                  className="w-full bg-transparent text-right text-sm font-medium text-foreground outline-none pl-2 py-2"
                />
                <span className="text-sm font-medium text-foreground px-2 shrink-0">€</span>
              </div>
            </div>
            <div className="flex justify-between text-xs sm:text-sm text-muted-foreground">
              <span>Mín. {formatNumber(config.minAmount)} €</span>
              <span>Máx. {formatNumber(config.maxAmount)} €</span>
            </div>
          </div>

          {/* Time slider */}
          <div className="mb-4">
            <div className="flex items-center gap-4 mb-2">
              <div className="flex-1">
                <input
                  type="range"
                  min={config.minMonths}
                  max={config.maxMonths}
                  step={timeStep}
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}
                  style={{ "--slider-progress": `${monthsProgress}%` } as React.CSSProperties}
                  className="w-full"
                />
              </div>
              <div className="border border-border rounded-lg w-[130px] shrink-0 text-center bg-card flex items-center">
                <input
                  ref={monthsInputRef}
                  type="text"
                  inputMode="numeric"
                  value={monthsInput}
                  onChange={(e) => setMonthsInput(e.target.value)}
                  onBlur={(e) => commitMonths(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { commitMonths((e.target as HTMLInputElement).value); (e.target as HTMLInputElement).blur(); } }}
                  className="w-full bg-transparent text-right text-sm font-medium text-foreground outline-none pl-2 py-2"
                />
                <span className="text-sm font-medium text-foreground px-2 shrink-0">
                  {config.timeUnit === "años" ? "años" : "meses"}
                </span>
              </div>
            </div>
            <div className="flex justify-between text-xs sm:text-sm text-muted-foreground">
              <span>
                Mín. {config.timeUnit === "años"
                  ? `${config.minMonths / 12} años`
                  : `${config.minMonths} meses`}
              </span>
              <div className="flex items-center gap-2">
                <span>
                  Máx. {config.timeUnit === "años"
                    ? `${config.maxMonths / 12} años`
                    : `${config.maxMonths} meses`}
                </span>
                {config.timeUnit === "meses" && (
                  <span className="text-primary font-medium">({results.years} años)</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right: Results panel */}
        <div className="bg-secondary/50 rounded-2xl p-6 sm:p-8">
          <div className="mb-4">
            <span className="text-4xl sm:text-5xl font-extrabold text-foreground">
              {results.euros}
            </span>
            <sup className="text-lg sm:text-xl font-bold text-foreground ml-0.5 relative -top-4">
              {results.cents.toString().padStart(2, "0")} €
            </sup>
          </div>

          <p className="text-sm sm:text-base text-foreground mb-4">
            Al mes durante <strong>{timeDisplay}</strong>
          </p>

          <p className="text-sm text-foreground mb-6">
            TIN: {TIN} % | <strong>TAE: {TAE} %</strong>
          </p>

          <p className="text-xs text-muted-foreground mb-6 leading-relaxed">
            * Las condiciones pueden variar según la entidad colaboradora o criterios de riesgo
            ajenos a EasyDebt
          </p>

          <a
            href={solicitaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-primary text-primary-foreground font-semibold py-3.5 rounded-lg hover:opacity-90 transition-opacity text-sm sm:text-base inline-flex items-center justify-center"
          >
            Solicita {formatNumber(amount)}€
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoanCalculator;
